import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const FAQ = () => {
  const faqs = [
    {
      question: "How fast can I get funded?",
      answer:
        "Most applications are approved within hours, and funding can be deposited as soon as the same business day. The exact timeline depends on the type of funding and how quickly you provide the required documents.",
    },
    {
      question: "Will applying affect my credit score?",
      answer:
        "No. We perform a soft credit pull during the initial application process, which does not impact your credit score. A hard pull may only occur if you decide to move forward with a specific funding offer.",
    },
    {
      question: "What documents do I need to apply?",
      answer:
        "Typically, we just need your basic business information and the last 3-4 months of bank statements. No extensive paperwork, tax returns, or business plans required for most funding options.",
    },
    {
      question: "What if I have bad credit?",
      answer:
        "We look at many factors beyond just credit score, including your business revenue, time in business, and cash flow. Many businesses with less-than-perfect credit qualify for funding through our programs.",
    },
    {
      question: "What are the minimum requirements to qualify?",
      answer:
        "Generally, you need to be in business for at least 6 months, have a minimum monthly revenue of $10,000, and have an active business bank account. Requirements may vary by funding type.",
    },
    {
      question: "How much funding can I qualify for?",
      answer:
        "Funding amounts range from $5,000 to $5 million depending on your business revenue, time in business, and the type of funding. Most businesses qualify for 1-2x their monthly revenue.",
    },
    {
      question:
        "What's the difference between a term loan and a line of credit?",
      answer:
        "A term loan provides a lump sum upfront that you repay over a fixed period. A line of credit gives you access to funds you can draw from as needed, and you only pay interest on what you use.",
    },
    {
      question: "What is a Merchant Cash Advance (MCA)?",
      answer:
        "An MCA provides upfront capital in exchange for a percentage of your future sales. Repayments are made automatically as a small percentage of daily credit card transactions, so payments flex with your revenue.",
    },
    {
      question: "Can I pay off my funding early?",
      answer:
        "Yes! Most of our funding options allow early payoff. Some products may offer discounts for early repayment. Ask your funding specialist about the specific terms for your offer.",
    },
    {
      question: "How is this different from a bank loan?",
      answer:
        "Traditional bank loans often require extensive documentation, perfect credit, and weeks of waiting. Our funding solutions are designed for speed and accessibility — faster approvals, simpler requirements, and funding in days, not months.",
    },
    {
      question: "Do I need collateral to get funded?",
      answer:
        "Most of our funding options are unsecured, meaning you don't need to put up collateral like property or equipment. Approval is based primarily on your business revenue and cash flow.",
    },
    {
      question: "Can I have multiple funding products at once?",
      answer:
        "Yes, depending on your business qualifications. Many of our clients use a combination of products — for example, a term loan for a large purchase and a line of credit for ongoing working capital needs.",
    },
  ];

  return (
    <>
      <SEO
        title="FAQ"
        description="Frequently asked questions about Lloyd Funding's business financing, application process, approval times, and funding options."
        canonical="/faq"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-12 sm:px-6 sm:pt-24 sm:pb-12 lg:px-8 lg:pt-24 lg:pb-16">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl">
              Frequently Asked <span className="text-blue-700">Questions</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Got questions? We've got answers. Find everything you need to know
              about our funding solutions below.
            </p>
          </div>
        </div>

        {/* Wave divider */}
        <div className="pointer-events-none relative h-10 overflow-hidden">
          <svg
            className="absolute bottom-0 left-0 h-16 w-full"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64 C288,110 576,110 864,80 C1152,50 1296,50 1440,80 L1440,120 L0,120 Z"
              fill="#FFFFFF"
            />
          </svg>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group rounded-2xl border border-slate-100 bg-slate-50 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 p-5 text-slate-800">
                  <h2 className="text-lg font-semibold">{faq.question}</h2>
                  <svg
                    className="size-5 shrink-0 text-blue-600 transition-transform duration-300 group-open:-rotate-180"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>
                <p className="px-5 pb-5 text-slate-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold text-slate-800 sm:text-3xl">
              Still have questions?
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Our funding experts are here to help. Get in touch and we'll
              answer any questions you have.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/contact#application-form"
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 transition"
              >
                Apply Now
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-xl bg-white px-6 py-3 text-sm font-semibold text-blue-600 ring-1 ring-blue-600 hover:bg-blue-50 transition"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;
